import React, { useState, useEffect, useMemo } from "react";
import Validate from "../Validate/Validate";
import { useLocation, useNavigate } from "react-router-dom";
import VerificationNavbar from "./VerificationNavbar";
import ProgressStepper from "./ProgressStepper";
import { useTranslation } from "react-i18next";
import { useTheme } from "../../store/ThemeContext";

const CombinedValidate = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { state } = location;
  const { t } = useTranslation();
  const { verificationMethods } = useTheme();

  const [currentStep, setCurrentStep] = useState(0);
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);

  const [activeSection, setActiveSection] = useState(
    state?.showLegacySection || "vc",
  );

  useEffect(() => {
    if (!verificationMethods) return;

    const hasWithQr =
      Array.isArray(verificationMethods.with_qr) &&
      verificationMethods.with_qr.length > 0;
    const hasWithoutQr =
      Array.isArray(verificationMethods.without_qr) &&
      verificationMethods.without_qr.length > 0;

    if (hasWithQr && activeSection === "vc") {
      // already on vc, which is correct
      return;
    }

    if (!hasWithQr && hasWithoutQr) {
      setActiveSection("legacy");
    } else if (!hasWithQr && !hasWithoutQr) {
      setActiveSection("vc");
    } else if (hasWithQr && activeSection === "legacy") {
      if (!state?.showLegacySection) {
        setActiveSection("vc");
      }
    }
  }, [verificationMethods, activeSection, state?.showLegacySection]);

  const steps = useMemo(
    () => ({
      vc: [t("verify:upload"), t("verify:payment"), t("verify:summary")],
      legacy: [
        t("verify:select_certificate"),
        t("verify:details"),
        t("verify:payment"),
        t("verify:summary"),
      ],
      datavalidate: [
        t("verify:select_certificate"),
        t("verify:details"),
        t("verify:payment"),
        t("verify:summary"),
      ],
    }),
    [t],
  );

  useEffect(() => {
    const handleBeforeUnload = (e) => {
      if (hasUnsavedChanges) {
        e.preventDefault();
        e.returnValue = `${t("verify:unsaved_changes_warning")}`;
      }
    };

    window.addEventListener("beforeunload", handleBeforeUnload);
    return () => {
      window.removeEventListener("beforeunload", handleBeforeUnload);
    };
  }, [hasUnsavedChanges]);

  const handleSectionChange = (section) => {
    if (hasUnsavedChanges) {
      const confirmNavigation = window.confirm(
        `${t("verify:unsaved_changes_warning")}`,
      );
      if (!confirmNavigation) return;
    }
    setActiveSection(section);
    setCurrentStep(section === "legacy" || section === "datavalidate" ? 0 : 0);
    setHasUnsavedChanges(false);
    if (section === "legacy") {
      navigate("/verifier/legacy/certificate-details");
    }
  };

  useEffect(() => {
    const handlePopState = (e) => {
      if (hasUnsavedChanges) {
        const confirmNavigation = window.confirm(
          `${t("verify:unsaved_changes_warning")}`,
        );
        if (!confirmNavigation) {
          e.preventDefault();
          navigate(location.pathname, { replace: true });
        }
      }
    };

    window.addEventListener("popstate", handlePopState);
    return () => {
      window.removeEventListener("popstate", handlePopState);
    };
  }, [hasUnsavedChanges, navigate, location.pathname]);

  useEffect(() => {
    if (currentStep === 1 || currentStep === 2 || currentStep === 3) {
      setHasUnsavedChanges(true);
    } else {
      setHasUnsavedChanges(false);
    }
  }, [currentStep]);

  return (
    <div id="combined-validate-container" className="pt-[6rem] bg-white px-4">
      <VerificationNavbar
        activeSection={activeSection}
        handleSectionChange={handleSectionChange}
      />
      <ProgressStepper steps={steps[activeSection]} currentStep={currentStep} />
      <div className="validate-content">
        {activeSection === "vc" && <Validate setCurrentStep={setCurrentStep} />}
        {activeSection === "legacy" &&
          navigate("/verifier/legacy/certificate-details")}
      </div>
    </div>
  );
};

export default CombinedValidate;
